import { Trans, useLingui } from "@lingui/react/macro";
import { updateDoubleStaffing } from "@/api/Activities";
import { FilledButton } from "@medoma/components/Button/Button";
import Checkbox from "@/components/Checkbox/Checkbox";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import Form from "@/components/Form/Form";
import { Loading } from "@medoma/components/Loading/Loading";
import { deducedError } from "@/Utils/ErrorUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";

export const EditActivityDoubleStaffing = ({
  currentDoubleStaffing,
  onSuccess,
  activityId,
}: {
  currentDoubleStaffing: boolean;
  onSuccess: () => void;
  activityId: string;
}) => {
  const { t } = useLingui();
  const {
    formState: { errors, isDirty },
    getValues,
    handleSubmit,
    register,
    reset,
    setError,
  } = useForm({
    defaultValues: {
      doubleStaffing: currentDoubleStaffing,
    },
  });

  const queryClient = useQueryClient();
  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: (doubleStaffing: boolean) =>
      updateDoubleStaffing(activityId, doubleStaffing),
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      onSuccess();
    },
  });

  return (
    <Form
      onSubmit={handleSubmit((formData) => {
        mutate(formData.doubleStaffing);
      })}
    >
      {isPending || isSuccess ? (
        <Loading message={t`Ändrar dubbelbemanning`} />
      ) : (
        <>
          {errors.root?.server?.message && !isDirty ? (
            <ErrorMessage message={errors.root.server.message} />
          ) : null}
          <Form.Row>
            <Checkbox
              label={{ text: t`Dubbelbemanning` }}
              {...register("doubleStaffing")}
            />
          </Form.Row>
          {isDirty ? (
            <FilledButton type="submit">
              <Trans>Spara ändringar</Trans>
            </FilledButton>
          ) : null}
        </>
      )}
    </Form>
  );
};
